import { useState } from "react";
import { Plus, Minus, PawPrint } from "lucide-react";
import { monthsSince, ageText, petAgeError } from "@/lib/petAge";
import { Title, Eyebrow, Nav, Field, Choice, Chip, PetAvatar } from "./ui";

const NEUTER = [["yes", "Yes"], ["no", "No"], ["unknown", "Not sure"]];
const TAGS = ["Nervous around noise", "Loves treats", "Skin sensitivities", "First visit", "Senior pet", "Doesn't love other dogs"];

const petAge = (p) => ageText(monthsSince(p.age_dob) ?? monthsSince(p.dob));

export function Pets({ pets, newPets, selected, onToggle, onAdd, onNext, onBack, busy }) {
  const all = [...pets, ...newPets.map((p) => ({ ...p, id: p.tmp, isNew: true }))];
  return (
    <div data-testid="sb-step-pets">
      <Eyebrow>Your companions</Eyebrow>
      <div className="mt-3"><Title sub="Choose everyone joining us today.">Who&rsquo;s visiting us today?</Title></div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {all.map((p) => (
          <Choice key={p.id} selected={selected.includes(p.id)} onClick={() => onToggle(p.id)} testid={`sb-pet-${p.id}`} className="flex items-center gap-4 p-5">
            <PetAvatar pet={p} />
            <div className="min-w-0 pr-8">
              <p className="sb-serif break-words text-2xl font-semibold leading-tight">{p.name}</p>
              <p className="break-words text-sm text-[var(--sb-muted)]">{[p.breed || p.species, p.isNew ? (p.age_years != null || p.age_months != null ? ageText((p.age_years || 0) * 12 + (p.age_months || 0)) : "") : petAge(p)].filter(Boolean).join(" · ") || "Tap to select"}</p>
            </div>
          </Choice>
        ))}
        <button type="button" onClick={onAdd} data-testid="sb-add-pet"
          className="sb-lift flex min-h-[104px] items-center gap-4 rounded-3xl border-2 border-dashed border-[var(--sb-lav-line)] bg-white/60 p-5 text-left">
          <span className="grid h-16 w-16 place-items-center rounded-2xl bg-[var(--sb-lav-bg)] text-[var(--sb-plum)]"><Plus className="h-6 w-6" /></span>
          <span><span className="block font-semibold">Add a new friend</span><span className="text-sm text-[var(--sb-muted)]">Register another pet</span></span>
        </button>
      </div>
      <Nav onBack={onBack} onNext={onNext} disabled={!selected.length} busy={busy} />
    </div>
  );
}

function Stepper({ label, value, max, onChange, testid }) {
  const v = value ?? 0;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-white px-4 py-3 ring-1 ring-[var(--sb-line)]" data-testid={testid}>
      <span className="font-semibold">{label}</span>
      <div className="flex items-center gap-3">
        <button type="button" aria-label={`Fewer ${label}`} onClick={() => onChange(Math.max(0, v - 1))} className="grid h-12 w-12 place-items-center rounded-full bg-[var(--sb-lav-bg)] text-[var(--sb-plum)]" data-testid={`${testid}-dec`}><Minus className="h-5 w-5" /></button>
        <span className="sb-serif w-10 text-center text-3xl font-semibold" data-testid={`${testid}-value`}>{value ?? "–"}</span>
        <button type="button" aria-label={`More ${label}`} onClick={() => onChange(Math.min(max, value == null ? 1 : v + 1))} className="grid h-12 w-12 place-items-center rounded-full bg-[var(--sb-lav-bg)] text-[var(--sb-plum)]" data-testid={`${testid}-inc`}><Plus className="h-5 w-5" /></button>
      </div>
    </div>
  );
}

export function AgeNeuter({ value, onChange, needAge = true, needNeuter = true, tried, prefix }) {
  const ageErr = needAge ? (petAgeError({ ...value, age_years: value.age_years ?? "", age_months: value.age_months ?? "" })
    || ((value.age_years || 0) * 12 + (value.age_months || 0) === 0 ? "Please enter an age of at least 1 month." : "")) : "";
  return (
    <div className="grid gap-6">
      {needAge && <Field label="How old are they?" error={tried ? ageErr : ""}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Stepper label="Years" value={value.age_years} max={40} onChange={(n) => onChange({ age_years: n, age_months: value.age_months ?? 0 })} testid={`${prefix}-years`} />
          <Stepper label="Months" value={value.age_months} max={11} onChange={(n) => onChange({ age_months: n, age_years: value.age_years ?? 0 })} testid={`${prefix}-months`} />
        </div>
      </Field>}
      {needNeuter && <Field label="Spayed or neutered?" error={tried && !value.neuter_status ? "Please choose one." : ""}>
        <div className="flex flex-wrap gap-3">{NEUTER.map(([k, l]) => <Chip key={k} selected={value.neuter_status === k} onClick={() => onChange({ neuter_status: k })} testid={`${prefix}-neuter-${k}`}>{l}</Chip>)}</div>
      </Field>}
    </div>
  );
}

export const ageNeuterOk = (v, needAge = true, needNeuter = true) =>
  (!needAge || (v.age_years != null && !petAgeError(v) && (v.age_years || 0) * 12 + (v.age_months || 0) > 0)) && (!needNeuter || !!v.neuter_status);

export function PetForm({ onSave, onBack }) {
  const [p, setP] = useState({ name: "", species: "Dog", breed: "", gender: "", age_years: null, age_months: null, neuter_status: "", vax: "" });
  const [tried, setTried] = useState(false);
  const set = (x) => setP((s) => ({ ...s, ...x }));
  const ok = p.name.trim() && ageNeuterOk(p);
  return (
    <div data-testid="sb-step-petform">
      <Eyebrow>New friend</Eyebrow>
      <div className="mt-3"><Title sub="Tell us about them so we can give the best care.">Let&rsquo;s meet your furry friend.</Title></div>
      <div className="mt-10 grid max-w-2xl gap-6">
        <Field label="Their name" error={tried && !p.name.trim() ? "What should we call them?" : ""}>
          <input className="sb-input" value={p.name} maxLength={60} onChange={(e) => set({ name: e.target.value })} data-testid="sb-pet-name" placeholder="e.g. Bruno" />
        </Field>
        <Field label="They are a…"><div className="flex flex-wrap gap-3">{["Dog", "Cat", "Other"].map((s) => <Chip key={s} selected={p.species === s} onClick={() => set({ species: s })} testid={`sb-pet-species-${s.toLowerCase()}`}>{s}</Chip>)}</div></Field>
        <Field label="Breed (optional)"><input className="sb-input" value={p.breed} maxLength={60} onChange={(e) => set({ breed: e.target.value })} data-testid="sb-pet-breed" placeholder="e.g. Golden Retriever" /></Field>
        <Field label="Gender (optional)"><div className="flex flex-wrap gap-3">{[["male", "Boy"], ["female", "Girl"]].map(([k, l]) => <Chip key={k} selected={p.gender === k} onClick={() => set({ gender: p.gender === k ? "" : k })} testid={`sb-pet-gender-${k}`}>{l}</Chip>)}</div></Field>
        <AgeNeuter value={p} onChange={set} tried={tried} prefix="sb-pet" />
        <Field label="Are their vaccinations up to date?" hint="Reception will check the records with you.">
          <div className="flex flex-wrap gap-3">{[["yes", "Yes"], ["no", "No"], ["unsure", "Not sure"]].map(([k, l]) => <Chip key={k} selected={p.vax === k} onClick={() => set({ vax: k })} testid={`sb-pet-vax-${k}`}>{l}</Chip>)}</div>
        </Field>
      </div>
      <Nav onBack={onBack} onNext={() => { setTried(true); if (ok) onSave({ ...p, name: p.name.trim(), tmp: `new_${Date.now()}` }); }} nextLabel="Add to visit" testid="sb-pet-save" />
    </div>
  );
}

export function PetDetails({ pets, missing, fix, onChange, onNext, onBack }) {
  const [tried, setTried] = useState(false);
  const list = pets.filter((p) => missing[p.id]?.length);
  const ok = list.every((p) => ageNeuterOk(fix[p.id] || {}, missing[p.id].includes("age"), missing[p.id].includes("neuter_status")));
  return (
    <div data-testid="sb-step-petdetails">
      <Eyebrow>A quick update</Eyebrow>
      <div className="mt-3"><Title sub="We're missing a detail or two for the best care.">Help us get to know them</Title></div>
      <div className="mt-10 grid gap-6">
        {list.map((p) => (
          <div key={p.id} className="sb-card p-6" data-testid={`sb-petfix-${p.id}`}>
            <div className="mb-5 flex items-center gap-3"><PetAvatar pet={p} size="h-12 w-12" /><p className="sb-serif text-2xl font-semibold">{p.name}</p></div>
            <AgeNeuter value={fix[p.id] || {}} onChange={(x) => onChange(p.id, x)} tried={tried} prefix={`sb-fix-${p.id}`}
              needAge={missing[p.id].includes("age")} needNeuter={missing[p.id].includes("neuter_status")} />
          </div>
        ))}
      </div>
      <Nav onBack={onBack} onNext={() => { setTried(true); if (ok) onNext(); }} />
    </div>
  );
}

export function Notes({ notes, tags, onChange, onNext, onBack }) {
  const toggle = (t) => onChange({ tags: tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t] });
  return (
    <div data-testid="sb-step-notes">
      <Eyebrow><PawPrint className="mr-1 inline h-3.5 w-3.5" /> Their comfort matters</Eyebrow>
      <div className="mt-3"><Title sub="Optional — anything that helps us make them feel at home.">Anything special we should know?</Title></div>
      <div className="mt-10 flex max-w-3xl flex-wrap gap-3">{TAGS.map((t) => <Chip key={t} selected={tags.includes(t)} onClick={() => toggle(t)} testid={`sb-tag-${t.split(" ")[0].toLowerCase()}`}>{t}</Chip>)}</div>
      <textarea value={notes} maxLength={400} onChange={(e) => onChange({ notes: e.target.value })} data-testid="sb-notes"
        placeholder="Tell us in your own words…" className="sb-input sb-textarea mt-6 max-w-3xl" rows={4} />
      <Nav onBack={onBack} onNext={onNext} />
    </div>
  );
}
